/**
 * @fileoverview Companion Cases Step - Links matter to companion case groups.
 *
 * Allows users to:
 * 1. Indicate no companions (saves section as 'not_found' on form submit)
 * 2. Start a new companion group (makes this case primary on form submit)
 * 3. Link to an existing companion group (with primary picker for standalone matters)
 *
 * Shows suggestions panel for potential companions based on:
 * - Minor detection (client under 18)
 * - Similar names from same firm in past 48 hours
 *
 * Follows the standard ingestion step pattern:
 * - Options are temporary selections until form submit
 * - Form submit (triggered by Continue/Back/Sidebar) persists the choice
 */
import { CheckCircle2 } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import {
  useGetMatterCompanionsQuery,
  useGetMatterDetailsQuery,
  usePatchMatterCompanionMembersMutation,
  usePatchMatterCompanionsMutation,
} from '@/services/api/mattersService';
import type { IngestionSections } from '@/services/types/case-ingestion-types';

import { usePotentialCompanions, useStepSave } from '../../hooks';
import { IngestionStepLayout } from '../../layout';
import { IngestionStepSkeleton } from '../../skeletons';
import {
  type CompanionChoice,
  CompanionOptionSelector,
} from './CompanionOptionSelector';
import { LinkToExistingGroup } from './LinkToExistingGroup';
import { PotentialCompanionsPanel } from './PotentialCompanionsPanel';
import { PrimaryPickerDialog } from './PrimaryPickerDialog';

interface CompanionCasesStepProps {
  matterId: string;
  sectionStatuses: IngestionSections;
  onSaveComplete?: (success: boolean) => void;
}

/**
 * CompanionCasesStep - Step 3 of the case ingestion flow.
 *
 * Users must explicitly choose one of three options:
 * - No companions (saves section as 'not_found' on Continue)
 * - This is the first case (makes primary + saves section as 'found' on Continue)
 * - Link to another case (clicking "Link to Case" button does the linking)
 */
export const CompanionCasesStep: React.FC<CompanionCasesStepProps> = ({
  matterId,
  sectionStatuses,
  onSaveComplete,
}) => {
  // Hydration refs
  const hasHydratedChoice = useRef(false);

  // Local state
  const [selectedChoice, setSelectedChoice] = useState<CompanionChoice | null>(null);
  const [showPrimaryPicker, setShowPrimaryPicker] = useState(false);
  const [pickerTargetMatterId, setPickerTargetMatterId] = useState('');
  const [pickerTargetMatterName, setPickerTargetMatterName] = useState('');

  // Hooks
  const { saveStep, isSaving } = useStepSave();

  // API queries
  const { data: matter, isLoading: isLoadingMatter } = useGetMatterDetailsQuery(matterId);
  const {
    data: companions,
    isLoading: isLoadingCompanions,
    refetch: refetchCompanions,
  } = useGetMatterCompanionsQuery(matterId);

  // API mutations
  const [makePrimary, { isLoading: isMakingPrimary }] = usePatchMatterCompanionsMutation();
  const [patchMembers, { isLoading: isPatchingMembers }] =
    usePatchMatterCompanionMembersMutation();

  const isLinking = isMakingPrimary || isPatchingMembers || isSaving;

  // Extract client data for suggestions
  const client = matter?.clients?.[0];
  const clientDob = client?.date_of_birth ?? null;
  const clientLastName = client?.last_name ?? null;
  const firmId = matter?.firm?.id ?? null;
  const matterName = matter?.name ?? 'This case';

  // Fetch potential companions
  const { isMinor, similarMatters, isLoading: isLoadingSuggestions } = usePotentialCompanions(
    matterId,
    clientDob,
    clientLastName,
    firmId,
  );

  // Check current companion status
  const isPrimary =
    companions?.members.some((m) => m.id === matterId && m.is_primary) ?? false;
  const isGroupMember = (companions?.members.length ?? 0) > 1;
  const hasCompanionStatus = isPrimary || isGroupMember;

  // Hydrate selected choice from section status or companion API status
  useEffect(() => {
    if (hasHydratedChoice.current) return;
    // Wait for companions data to be loaded before hydrating
    if (!companions) return;

    // If already in a group, set choice based on status
    if (isPrimary) {
      hasHydratedChoice.current = true;
      setSelectedChoice('start_group');
      return;
    }
    if (isGroupMember) {
      hasHydratedChoice.current = true;
      setSelectedChoice('link_existing');
      return;
    }

    // If section status indicates previous choice
    if (sectionStatuses.companion_cases === 'not_found') {
      hasHydratedChoice.current = true;
      setSelectedChoice('no_companions');
    } else if (sectionStatuses.companion_cases === 'found') {
      // Section was marked found but not in group - might have been undone
      // Let user re-choose
      hasHydratedChoice.current = true;
    }
  }, [companions, isPrimary, isGroupMember, sectionStatuses.companion_cases]);

  /**
   * Form submit handler - called when navigating away (Continue/Back/Sidebar).
   * Persists the selected choice if needed.
   */
  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // If already in a companion group, ensure section status is saved in audit record
    if (hasCompanionStatus) {
      if (sectionStatuses.companion_cases === null) {
        // Companion group exists but audit record hasn't been updated yet
        // (e.g., linked from another case's ingestion flow)
        const success = await saveStep(
          async () => {
            // No domain operation needed - already in group
          },
          { companion_cases: 'found' },
          { successMessage: 'Companion status confirmed' },
        );
        onSaveComplete?.(success);
      } else {
        onSaveComplete?.(true);
      }
      return;
    }

    // If no choice made, just navigate (user can come back later)
    if (!selectedChoice) {
      onSaveComplete?.(true);
      return;
    }

    // Handle "No companions" - save section as 'not_found'
    if (selectedChoice === 'no_companions') {
      const success = await saveStep(
        async () => {
          // No domain operation needed
        },
        { companion_cases: 'not_found' },
        { successMessage: 'Marked as no companions' },
      );
      onSaveComplete?.(success);
      return;
    }

    // Handle "This is the first case" - make primary + save section as 'found'
    if (selectedChoice === 'start_group') {
      try {
        await makePrimary({
          matter_id: matterId,
          make_primary: true,
        }).unwrap();

        const success = await saveStep(
          async () => {
            // Domain operation already done above
          },
          { companion_cases: 'found' },
          { successMessage: 'Companion group started. This case is now the primary.' },
        );

        refetchCompanions();
        onSaveComplete?.(success);
      } catch {
        toast.error('Failed to start companion group');
        onSaveComplete?.(false);
      }
      return;
    }

    // Handle "Link to existing" - if they haven't actually linked yet, just navigate
    // The actual linking happens when they click "Link to Case" button
    if (selectedChoice === 'link_existing') {
      onSaveComplete?.(true);
    }
  };

  // Handle direct link to an existing primary matter (no picker needed)
  // This is called when user clicks "Link to Case" button - saves immediately
  const handleLinkToPrimary = async (primaryMatterId: string) => {
    try {
      // Add current matter to the existing primary's group
      await patchMembers({
        matter_id: primaryMatterId,
        add: [matterId],
      }).unwrap();

      // Save section status as 'found'
      await saveStep(
        async () => {
          // Domain operation already done above
        },
        { companion_cases: 'found' },
        { successMessage: 'Linked to companion group' },
      );

      refetchCompanions();
    } catch {
      toast.error('Failed to link to companion group');
    }
  };

  // Handle link to standalone matter - show primary picker
  const handleLinkWithPicker = (targetMatterId: string, targetMatterName: string) => {
    // Store target info for the picker dialog
    setPickerTargetMatterId(targetMatterId);
    setPickerTargetMatterName(targetMatterName);
    setShowPrimaryPicker(true);
  };

  // Handle primary picker confirmation - saves immediately since it's an explicit action
  const handlePrimaryPickerConfirm = async (primaryChoice: 'current' | 'target') => {
    try {
      const primaryMatterId = primaryChoice === 'current' ? matterId : pickerTargetMatterId;
      const memberMatterId = primaryChoice === 'current' ? pickerTargetMatterId : matterId;

      // Make the chosen matter primary
      await makePrimary({
        matter_id: primaryMatterId,
        make_primary: true,
      }).unwrap();

      // Add the other matter to the group
      await patchMembers({
        matter_id: primaryMatterId,
        add: [memberMatterId],
      }).unwrap();

      // Save section status as 'found'
      await saveStep(
        async () => {
          // Domain operations already done above
        },
        { companion_cases: 'found' },
        {
          successMessage:
            primaryChoice === 'current'
              ? `Companion group created. This case is the primary.`
              : `Linked to companion group. ${pickerTargetMatterName} is the primary.`,
        },
      );

      setShowPrimaryPicker(false);
      refetchCompanions();
    } catch {
      toast.error('Failed to create companion group');
    }
  };

  // Handle quick link from suggestions panel
  const handleQuickLink = (targetMatterId: string) => {
    const targetMatter = similarMatters.find((m) => m.id === targetMatterId);
    if (!targetMatter) return;

    // If target is already a primary, link directly
    if (targetMatter.is_companion_primary) {
      handleLinkToPrimary(targetMatterId);
    } else {
      // Target is standalone - show primary picker
      handleLinkWithPicker(targetMatterId, targetMatter.name);
    }
  };

  // Loading state
  if (isLoadingMatter || isLoadingCompanions) {
    return <IngestionStepSkeleton />;
  }

  return (
    <IngestionStepLayout
      title="Companion Cases"
      description="Link this case to related cases in a companion set"
    >
      <form
        id="ingestion-step-form"
        onSubmit={handleFormSubmit}
        className="space-y-2"
      >
        {/* Show current companion status if already in a group */}
        {hasCompanionStatus && (
          <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-md border border-green-200 bg-green-50">
            <CheckCircle2 className="h-3.5 w-3.5 text-green-600 flex-shrink-0" />
            <p className="text-xs text-green-800">
              {isPrimary
                ? 'This case is the primary of a companion group.'
                : 'Linked to companion group.'}
            </p>
          </div>
        )}

        {/* Show group members if already in a group */}
        {hasCompanionStatus && companions && companions.members.length > 1 && (
          <div className="rounded-md border border-zinc-200 overflow-hidden">
            <div className="px-2 py-1.5 bg-zinc-50 border-b border-zinc-200">
              <p className="text-xs font-medium text-zinc-700">
                Group Members ({companions.members.length})
              </p>
            </div>
            <div className="divide-y divide-zinc-100">
              {companions.members.map((member) => (
                <div
                  key={member.id}
                  className="flex items-center justify-between px-2 py-1.5 bg-white text-xs"
                >
                  {member.id === matterId ? (
                    <span className="font-medium text-zinc-900">
                      {member.display_name} (this case)
                    </span>
                  ) : (
                    <Link
                      to={`/matters/${member.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      {member.display_name}
                    </Link>
                  )}
                  {member.is_primary && (
                    <span className="text-xs text-zinc-600 bg-zinc-100 px-1.5 py-0.5 rounded">
                      Primary
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Suggestions panel (only show if not already in a group) */}
        {!hasCompanionStatus && !isLoadingSuggestions && (
          <PotentialCompanionsPanel
            isMinor={isMinor}
            similarMatters={similarMatters}
            onLinkToMatter={handleQuickLink}
            isLinking={isLinking}
          />
        )}

        {/* Option selector (only show if not already in a group) */}
        {!hasCompanionStatus && (
          <>
            <div className="rounded-md border border-zinc-200 p-2 bg-white">
              <CompanionOptionSelector
                value={selectedChoice}
                onChange={setSelectedChoice}
                disabled={isLinking}
              />
            </div>

            {/* Link to existing UI */}
            {selectedChoice === 'link_existing' && (
              <LinkToExistingGroup
                firmId={firmId}
                currentMatterId={matterId}
                onLinkToPrimary={handleLinkToPrimary}
                onLinkWithPicker={handleLinkWithPicker}
                isLinking={isLinking}
              />
            )}
          </>
        )}

        {/* Primary Picker Dialog */}
        <PrimaryPickerDialog
          open={showPrimaryPicker}
          onOpenChange={setShowPrimaryPicker}
          currentMatterName={matterName}
          targetMatterName={pickerTargetMatterName}
          onConfirm={handlePrimaryPickerConfirm}
          isLoading={isLinking}
        />
      </form>
    </IngestionStepLayout>
  );
};
